import { axiosClient } from "@/lib/axiosClient";

// =========================
// Interfaces
// =========================

export interface User {
    user_id: number;
    name: string;
    email: string;
    role_id: number;
    is_active: boolean;
    is_verified: boolean;
    is_changepass?: number | boolean;
    // Tambahkan field lain sesuai respon backend
    created_at?: string;
    updated_at?: string;
}

export interface LoginResponse {
    message: string;
    access_token: string;
    token_type: string;
    expires_in: number; // Backend Anda sekarang mengirimkan ini
    user: User;
}

interface GetCurrentUserResponse {
    user: User | null;
}

interface RefreshTokenResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
}

// =========================
// API Functions
// =========================

/**
 * Login user.
 * Backend akan men-set HttpOnly Cookie untuk Refresh Token.
 * Frontend menerima Access Token (JSON) untuk dipakai sementara.
 */
export const login = async (
    email: string,
    password: string
): Promise<LoginResponse> => {
    const res = await axiosClient.post<LoginResponse>("/login", { email, password });

    // ❌ HAPUS: localStorage.setItem(...) 
    // Kita tidak simpan di localStorage karena tidak aman (XSS).
    // Penyimpanan token ditangani oleh AuthContext (Memory).

    return res.data;
};

/**
 * Fitur Login As untuk Admin (Impersonate)
 */
export const loginAs = async (userId: number): Promise<LoginResponse> => {
    const res = await axiosClient.post<LoginResponse>(`/login-as/${userId}`);
    return res.data;
};

/**
 * Mendapatkan data user yang sedang login.
 * Menggunakan Access Token yang ada di Memory.
 */
export const getCurrentUser = async (): Promise<User | null> => {
    try {
        const res = await axiosClient.get<GetCurrentUserResponse>("/me");
        return res.data?.user ?? null;
    } catch (error) {
        console.error("Gagal mendapatkan current user:", error);
        // Jika error 401, berarti token expired/tidak ada -> return null
        return null;
    }
};

/**
 * Logout user.
 * Menghapus Refresh Token di Backend & Browser Cookie.
 */
export const logout = async (): Promise<boolean> => {
    try {
        await axiosClient.post("/logout");

        // ❌ HAPUS: localStorage.removeItem(...) -> Tidak perlu
        // ❌ HAPUS: queryClient.removeQueries(...) -> Pindahkan ke UI (AuthButton/Context)

        return true;
    } catch (error) {
        console.error("Logout gagal:", error);
        return false;
    }
};

/**
 * ✅ FUNGSI BARU: Refresh Session Manual.
 * Dipakai oleh AuthContext saat aplikasi pertama kali dimuat (F5).
 * Browser otomatis mengirim cookie, server membalas dengan Access Token baru.
 */
export const refreshSession = async (): Promise<string | null> => {
    try {
        const res = await axiosClient.post<RefreshTokenResponse>("/refresh");
        return res.data.access_token;
    } catch (error) {
        console.error("Gagal refresh session:", error);
        // Jika gagal refresh (cookie expired/invalid), user dianggap logout
        return null;
    }
};